import React, { useState, useEffect, useMemo } from 'react';
import { Avatar, Button, createTableColumn, Field, Input, InputOnChangeData, makeStyles, TableCellLayout, tokens } from '@fluentui/react-components';
import { CustomerSearch } from '../components/CustomerSearch';
import { type ICustomer } from './../IDB';
import { Server } from "../apis";
import { Organization20Regular } from '@fluentui/react-icons';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { useNavigate } from 'react-router';
import { licenseMap } from './LicenseBuilder';
import { ViewLicenseDialog } from '../components/ViewLicenseDialog';

const useStyles = makeStyles({
    container: {
        marginLeft: '260px', // Width of navbar
        maxHeight: 'calc(100vh - 30px)',
        overflow: 'auto',
        padding: tokens.spacingVerticalXXL,
        '& 	.fui-Field label': {
            fontWeight: 'bold'
        }
    },
    renewDialog: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: tokens.spacingHorizontalMNudge
    },
    renewField: {
        marginTop: tokens.spacingVerticalXXXL,
        flexGrow: 1
    },
    hostIdInput: {
        width: '100%'
    },
    footer: {
        marginTop: tokens.spacingVerticalXXXL,
        '& button': {
            width: 'min-content',
            margin: '0px auto'
        }
    },
});

export const RenewLicense = () => {
    const styles = useStyles();
    const server = useMemo(() => Server.getInstance(), []);
    const navigate = useNavigate();
    const [selectedCustomer, setSelectedCustomer] = useState<ICustomer>();
    const [hostid, setHostid] = useState<string>("");
    const [expiry, setExpiry] = useState<string>("");
    const [invoice, setInvoice] = useState<string>("");
    const [error, setError] = useState<string>("");
    const [licenseFileElement, setLicenseFileElement] = useState<React.JSX.Element>();

    const onSelected = (customer: ICustomer) => {
        setSelectedCustomer(customer);
    };

    const [defaultCustomerSelection, setDefaultCustomerSelection] = useState<string>();

    useEffect(() => {
        const hash = window.location.hash;
        if (hash && hash.length > 1) {
            const code = hash.substring(1);
            let foundLic = null;
            server.getCustomers(null, null, code)
                .then((customers) => {
                    for (let c = 0; c < customers.length; c += 1) {
                        foundLic = customers[c].licenses.find((lic) => lic.code === code);
                        if (foundLic) {
                            setSelectedCustomer({
                                ...customers[c],
                                licenses: [foundLic]
                            });
                            setDefaultCustomerSelection(foundLic.code);
                            break;
                        }
                    }
                });
        }
    }, []);

    const searchLicenseColumns = [
        createTableColumn<ICustomer>({
            columnId: "company",
            renderHeaderCell: () => {
                return "Company";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout media={<Organization20Regular />}>
                        {item.company.name}
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<ICustomer>({
            columnId: "name",
            renderHeaderCell: () => {
                return "Recipient";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout aria-label="name" media={
                        <Avatar
                            aria-label={item.name}
                            name={item.name}
                        />
                    }>
                        {item.name}
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<ICustomer>({
            columnId: "code",
            renderHeaderCell: () => {
                return "Code";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout>
                        {item.licenses.length > 0 ? item.licenses[0].code : ""}
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<ICustomer>({
            columnId: "expiry",
            renderHeaderCell: () => {
                return "Expiration";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout>
                        {item.licenses.length > 0 ? new Date(item.licenses[0].expiry).toLocaleDateString() : ""}
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<ICustomer>({
            columnId: "type",
            renderHeaderCell: () => {
                return "License type";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout>
                        {item.licenses.length > 0
                            ? (item.licenses[0].licenseType === "NEW" && item.licenses[0].history.some((h) => h.indexOf('RENEWED') > -1)
                                ? "RENEWED"
                                : item.licenses[0].licenseType)
                            : ""}
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<ICustomer>({
            columnId: "invoice",
            renderHeaderCell: () => {
                return "Invoice";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout>
                        {item.licenses.length > 0 ? item.licenses[0].invoice : ""}
                    </TableCellLayout>
                );
            },
        }),
    ];

    const closePage = () => {
        if (selectedCustomer) {
            navigate(`/customers/${selectedCustomer.id}#${selectedCustomer.licenses[0].code}`);
        } else {
            navigate(`/customers`);
        }
    };

    const renewLicense = async () => {
        if (!expiry) {
            setError("Expiration date is required.");
            return;
        }
        if (selectedCustomer) {
            try {
                const licenseFile = await server.renewLicense(
                    selectedCustomer.licenses[0].code,
                    invoice,
                    expiry,
                    hostid
                );
                if (hostid && licenseFile) {
                    const fileContent = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), licenseFile], { type: "text/plain;charset=utf-8" });
                    const url = URL.createObjectURL(fileContent);
                    const anchorElement = <Button as="a" href={url} download="license.lic" data-text={licenseFile}>Download</Button>;
                    setLicenseFileElement(anchorElement);
                } else {
                    closePage();
                }
            } catch (err) {
                if (err.status === 405 && err.response.data.detail === "License is revoked") {
                    setError("License is revoked and cannot be renewed.")
                } else {
                    setError(err);
                }
            }
        }
    };

    let maxDate = new Date();
    if (selectedCustomer && selectedCustomer.licenses[0].licenseType === "EVAL") {
        maxDate.setDate(maxDate.getDate() + 60);
    } else {
        maxDate.setDate(maxDate.getDate() + (365 * 3));
    }

    return (
        <div className={styles.container}>
            <h2>Renew a Commercial License</h2>
            <CustomerSearch
                columns={searchLicenseColumns}
                filterResults={(customer) => {
                    if (customer.licenses.length > 0 && customer.licenses[0].licenseType === "NEW" && customer.licenses[0].revoked === false) {
                        return true;
                    }
                    return false;
                }}
                onSelected={onSelected}
                prepopulateSearch={defaultCustomerSelection ? true : false}
                preselectedValue={defaultCustomerSelection}
                searchLicenses={true}
                sortable={false}
            />
            <Field required label="Invoice" className={styles.renewField}>
                <Input placeholder='Enter an invoice number' onChange={(e, data: InputOnChangeData) => setInvoice(data.value)} />
            </Field>
            <Field required label="Expiration date" className={styles.renewField}>
                <DatePicker
                    placeholder="Select a new expiration date"
                    onSelectDate={(date: Date | null) => setExpiry(date?.toISOString() || "")}
                    minDate={new Date()}
                    maxDate={maxDate}
                    allowTextInput
                />
            </Field>
            <Field label="Host" className={styles.renewField}>
                <Input contentBefore="host=" className={styles.hostIdInput} placeholder="Optionally enter a new host id" onChange={(ev, data: InputOnChangeData) => setHostid(data.value)} />
            </Field>
            <Field className={styles.footer} validationMessage={error ? error : null} validationState={error ? 'error' : 'none'}>
                <Button appearance='primary' onClick={renewLicense}>Renew</Button>
            </Field>
            <ViewLicenseDialog licenseFileElement={licenseFileElement} close={() => { setLicenseFileElement(undefined); closePage(); }} />
        </div>
    );
};